import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { SnackbarContext } from 'components';
import * as React from 'react';
import {
  LightspeedMappingService,
  LightspeedShopId,
  LightspeedSnapshotsForShop,
} from 'services';

interface Props {
  shopId: LightspeedShopId;
  snapshots: LightspeedSnapshotsForShop | null;
  // Called after a successful paste so the parent can refetch snapshots
  // + state. We don't refetch internally — the parent owns the data.
  onPasted: () => void | Promise<void>;
}

const useStyles = makeStyles((theme) => ({
  pane: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  lastPasted: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  textarea: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  actions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& > * + *': { marginLeft: theme.spacing(1) },
  },
}));

export const PasteCatalogsTab: React.FC<Props> = ({
  shopId,
  snapshots,
  onPasted,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [itemsRaw, setItemsRaw] = React.useState('');
  const [piRaw, setPiRaw] = React.useState('');
  const [savingItems, setSavingItems] = React.useState(false);
  const [savingPi, setSavingPi] = React.useState(false);
  const [syncingPi, setSyncingPi] = React.useState(false);

  async function syncModifiersFromLightspeed() {
    try {
      setSyncingPi(true);
      const meta = await LightspeedMappingService.pullModifiers(shopId);
      snackbar.success(
        `Pulled ${meta.parsedCount} modifiers from Lightspeed`,
      );
      await onPasted();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSyncingPi(false);
    }
  }

  // The shop picker on the parent may flip — clear typed-but-not-saved
  // drafts so the user doesn't accidentally save a To Go paste against
  // Street Food.
  React.useEffect(() => {
    setItemsRaw('');
    setPiRaw('');
  }, [shopId]);

  async function save(payloadType: 'items' | 'production_instructions') {
    const raw = payloadType === 'items' ? itemsRaw : piRaw;
    const setSaving =
      payloadType === 'items' ? setSavingItems : setSavingPi;
    if (raw.trim() === '') {
      snackbar.error('Paste a catalog JSON first');
      return;
    }
    try {
      setSaving(true);
      const meta = await LightspeedMappingService.addSnapshot({
        shopId,
        payloadType,
        rawPayload: raw,
      });
      snackbar.success(
        `Saved ${meta.parsedCount} entries for shop ${shopId}`,
      );
      if (payloadType === 'items') setItemsRaw('');
      else setPiRaw('');
      await onPasted();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box>
      <Paper className={classes.pane}>
        <Box className={classes.paneHeader}>
          <Typography variant="h6">Items catalog</Typography>
          <Typography className={classes.lastPasted}>
            {snapshots?.itemsSnapshot
              ? `Last pasted: ${new Date(
                  snapshots.itemsSnapshot.pastedAt,
                ).toLocaleString()} · ${snapshots.itemsSnapshot.parsedCount} entries`
              : 'No catalog yet — paste below'}
          </Typography>
        </Box>
        <TextField
          multiline
          fullWidth
          rows={10}
          variant="outlined"
          placeholder='Paste the Lightspeed items catalog JSON here. Expected shape: [{"sku":"...","name":"..."}, ...]'
          value={itemsRaw}
          onChange={(e) => setItemsRaw(e.target.value)}
          InputProps={{ classes: { input: classes.textarea } }}
        />
        <Box className={classes.actions}>
          {savingItems && <CircularProgress size={20} />}
          <Button
            variant="contained"
            color="primary"
            disabled={savingItems || itemsRaw.trim() === ''}
            onClick={() => save('items')}
          >
            Save items snapshot
          </Button>
        </Box>
      </Paper>

      <Paper className={classes.pane}>
        <Box className={classes.paneHeader}>
          <Typography variant="h6">Production instructions catalog</Typography>
          <Typography className={classes.lastPasted}>
            {snapshots?.productionInstructionsSnapshot
              ? `Last updated: ${new Date(
                  snapshots.productionInstructionsSnapshot.pastedAt,
                ).toLocaleString()} · ${
                  snapshots.productionInstructionsSnapshot.parsedCount
                } modifiers`
              : 'No catalog yet — sync or paste below'}
          </Typography>
        </Box>
        <Box className={classes.actions} style={{ justifyContent: 'flex-start' }}>
          {syncingPi && <CircularProgress size={20} />}
          <Button
            variant="contained"
            color="secondary"
            disabled={syncingPi}
            onClick={syncModifiersFromLightspeed}
          >
            {syncingPi ? 'Syncing…' : 'Sync from Lightspeed'}
          </Button>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginLeft: 12 }}
          >
            Auto-pulls the latest modifiers via GET /peking/modifiers — no
            paste needed.
          </Typography>
        </Box>
        <Box style={{ marginTop: 16 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Or paste a snapshot manually (fallback for one-off testing):
          </Typography>
          <TextField
            multiline
            fullWidth
            rows={6}
            variant="outlined"
            placeholder='Paste the Lightspeed production-instructions JSON here.'
            value={piRaw}
            onChange={(e) => setPiRaw(e.target.value)}
            InputProps={{ classes: { input: classes.textarea } }}
          />
          <Box className={classes.actions}>
            {savingPi && <CircularProgress size={20} />}
            <Button
              variant="outlined"
              disabled={savingPi || piRaw.trim() === ''}
              onClick={() => save('production_instructions')}
            >
              Save pasted snapshot
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
